import { Anchor } from './MarketingShell';
import {
  Band, Card, DemoCallout, PricingTables, PilotOffer, Boundaries, Faq,
  Walkthrough, TrustGrid, Proof, FounderPortrait,
} from './sections';
import { SERVICES, PILOTS, money } from '@inbrief/shared';

/**
 * The public home page.
 *
 * Ordered as a single argument rather than a feature list: who it is for and
 * what changes for them, the problem in their words, what using it looks like,
 * proof you can touch, what it will not do, what it costs, and then an ask.
 *
 * Two rules have shaped most of the edits on this page and are worth keeping:
 *
 * Every heading is an outcome and every claim is checkable. "Four things, each
 * grounded in documents you have approved" describes the software; "Your whole
 * team can answer contract questions without asking the one person who read it"
 * describes what changes on a Tuesday, and is the same fact.
 *
 * Nothing on this page asserts something the code does not do. That constraint
 * is what makes the boundaries section persuasive rather than defensive — a
 * page that has been careful about the small claims has earned the big ones.
 */
export function Home() {
  return (
    <>
      <section className="mk-hero">
        <div className="mk-container mk-hero-inner">
          <div className="mk-hero-copy">
            <p className="mk-eyebrow">For GP practices, PCNs and federations</p>
            <h1 className="mk-hero-title">
              Everyone in your practice can answer contract questions. Not just the person who
              read it.
            </h1>
            <p className="mk-hero-lede">
              InBrief reads your service specifications and turns them into answers your whole
              team can use — with the passage each one came from shown alongside it. No patient
              data. No clinical advice. Nothing sent without a person approving it.
            </p>
            <div className="mk-hero-actions">
              <Anchor to="/contact" className="btn btn-primary">Book a demo</Anchor>
              <Anchor to="/demo" className="btn btn-secondary">Try it without signing up</Anchor>
            </div>
            <p className="mk-hero-small">
              Half an hour, against a specification you already hold. Twelve-week paid pilots from{' '}
              {money(PILOTS[0].priceGbp)}.
            </p>
          </div>

          <div className="mk-hero-shot">
            <img
              className="mk-hero-img"
              src="/product-preview.jpg"
              srcSet="/product-preview-720.jpg 720w, /product-preview.jpg 1280w"
              sizes="(max-width: 880px) calc(100vw - 48px), 520px"
              alt="The InBrief contract breakdown: a service specification's obligations,
                   indicators, deadlines and payment terms extracted into a summary, with the
                   section navigation down the left."
              width={1280}
              height={800}
              /*
                Spread, because the installed React types accept `fetchPriority`
                and this React version emits a warning for it and wants the
                lowercase HTML attribute. Written directly, one of the two
                complains; written like this, the correct attribute reaches the
                markup and neither does.
              */
              {...{ fetchpriority: 'high' }}
            />
          </div>
        </div>
      </section>

      {/*
        The strip under the hero used to be dashboard figures — documents
        processed, items awaiting approval — which are numbers about the
        software's own activity. They tell a visitor nothing about whether to
        buy it. These are the four things that change for the organisation, and
        each one is a claim the rest of the page has to make good on.
      */}
      <Band tone="tint">
        <ul className="mk-value">
          <li>
            <strong>Hours, not days</strong>
            <span>A specification read, structured and searchable in the time it takes to make coffee.</span>
          </li>
          <li>
            <strong>One source of truth</strong>
            <span>Approved once, used by everyone — reception to the management team.</span>
          </li>
          <li>
            <strong>Every answer checkable</strong>
            <span>The passage behind it is one click away, so nobody has to take it on faith.</span>
          </li>
          <li>
            <strong>Nothing lost when someone leaves</strong>
            <span>Contract knowledge stays with the organisation, not with the person who read it.</span>
          </li>
        </ul>
        <p className="mk-audience">
          Used by the people who have to read the specification and then make it happen — practice
          managers, PCN managers, service leads, care coordinators and reception teams.
        </p>
      </Band>

      <Band
        id="why"
        title="The specification nobody has time to read"
        lede="Dozens of pages. The obligations, the indicators, the deadlines and the payment
              conditions are spread through all of them — and the people who have to act on them
              are not the people who read them."
      >
        <div className="mk-three">
          <Card title="One or two people hold it all">
            <p>
              What the contract requires is understood by whoever read it. Everyone else asks
              them, waits, or guesses — and when they are on leave, the practice guesses.
            </p>
          </Card>
          <Card title="The same letter, written again">
            <p>
              Patient communications get redrafted from scratch by whoever is free, slightly
              differently every time, at whatever reading level they happen to write at.
            </p>
          </Card>
          <Card title="Nobody notices the contract changed">
            <p>
              A specification is reissued and the team carries on working from last year's, because
              the differences are on page thirty-one and nobody has time to diff two PDFs.
            </p>
          </Card>
        </div>
      </Band>

      <Band
        id="how-it-works"
        title="What using it actually looks like"
        tone="tint"
        lede="Four screens, in the order you meet them. These are real screenshots of the product,
              with invented data."
      >
        <Walkthrough />
        <div className="mk-hero-actions mk-walk-cta">
          <Anchor to="/demo" className="btn btn-primary">Open the interactive demo</Anchor>
        </div>
      </Band>

      <Band
        id="product"
        title="What your team gets"
        lede="Four capabilities, all working from documents your organisation has approved."
      >
        <div className="mk-two">
          <Card title="Contract knowledge that outlives the reader">
            <ul className="mk-list">
              <li>Obligations, KPIs, deadlines, eligibility rules and payment arrangements, extracted.</li>
              <li>Every item linked back to the passage it came from.</li>
              <li>Approved once, and kept as the organisation's source of truth.</li>
              <li>A reissued contract sits alongside the old one rather than quietly replacing it.</li>
            </ul>
          </Card>
          <Card title="Answers without interrupting anyone">
            <ul className="mk-list">
              <li>Staff ask in plain English and get an answer from approved documents.</li>
              <li>The supporting passage is shown, so the answer can be checked.</li>
              <li>Usable from reception, not just by the management team.</li>
              <li>An answer that cannot be grounded is refused rather than guessed.</li>
            </ul>
          </Card>
          <Card title="Patient communications, drafted consistently">
            <ul className="mk-list">
              <li>Letters, SMS, emails, leaflets and appointment guides at a chosen reading level.</li>
              <li>Why the patient is being contacted, and what happens next.</li>
              <li>Your practice's own details inserted automatically.</li>
              <li>Translation into the languages your population actually speaks.</li>
            </ul>
          </Card>
          <Card title="Nothing quietly out of date">
            <ul className="mk-list">
              <li>A reissued contract is uploaded against the same document, not over it.</li>
              <li>Previous versions stay readable, so you can see what changed.</li>
              <li>
                Each extraction stays attached to the version it was made from — an item approved
                last year does not start claiming to describe this year's contract.
              </li>
              <li>Retention is configurable, and deleting a document deletes the files too.</li>
            </ul>
          </Card>
        </div>
      </Band>

      <Band
        id="trust"
        title="Built for people who have to justify the decision"
        tone="tint"
        lede="Every line below is enforced in the software, not written for the website. The
              detail — data handling, tenant separation, audit, retention, model usage — is on the
              security page."
      >
        <TrustGrid />
        <div className="mk-hero-actions mk-walk-cta">
          <Anchor to="/security" className="btn btn-secondary">Security and governance</Anchor>
        </div>
      </Band>

      <Band
        id="demo"
        title="See it working"
        lede="The whole product, with invented data, in its own tab. Nothing to install and nothing
              to sign up for."
      >
        <DemoCallout heading="h3" />
      </Band>

      <Band id="proof" title="What we can show you today" tone="tint">
        <Proof />
      </Band>

      {/*
        Before the pricing, deliberately. A buyer deciding whether to take this
        seriously wants to know who is behind it before they are asked for
        money, and this is the one claim on the page that a competitor cannot
        copy. Cut to a single paragraph here — the full version is on /about,
        and a founder story that runs longer than the product explanation is a
        founder story on the wrong page.
      */}
      <Band id="founder" title="Built by someone who has delivered NHS services">
        <div className="mk-founder mk-founder-with-portrait">
          <FounderPortrait />
          <div className="mk-founder-text">
            <p>
              Louis Williams founded InBrief after several years in NHS primary care operations and
              transformation — managing contracts, service specifications and performance across GP
              practices and PCNs. The problem was always the same: too much operational knowledge
              locked in long documents, or held by a handful of experienced people.
            </p>
            <p className="mk-founder-more">
              <Anchor to="/about">More about InBrief and why it is built this way</Anchor>
            </p>
          </div>
        </div>
      </Band>

      <Band id="pricing" title="Pricing"
        lede="Per practice. Unlimited staff accounts on both plans, because charging per seat would
              keep the contract knowledge with the people who already have it.">
        <PricingTables heading="h3" />
        <div className="mk-spacer" />
        <PilotOffer />
      </Band>

      {/*
        Reworded after a reader stopped on it. "Optional services… none of them
        is required" leads with a negative and then negates again, which reads
        as pre-empting an accusation of upselling rather than as an offer.
        The independence is worth keeping — it is a real thing to know before
        you buy — so it is stated as a fact about the software instead of as a
        disclaimer about the services.
      */}
      <Band id="services" title="Expert support, when you want it"
        lede="Delivered by people and quoted per engagement, for when a contract needs more than
              reading. The software works on its own.">
        <ul className="mk-service-names">
          {SERVICES.slice(0, 9).map((s) => <li key={s.id}>{s.name}</li>)}
        </ul>
        <p className="mk-small">
          Also expert consultation and advisory retainers. All quoted per engagement.
        </p>
        <Anchor to="/services" className="btn btn-secondary">See all services</Anchor>
      </Band>

      <Band title="Where the product stops" tone="tint">
        <Boundaries />
        <p className="mk-small mk-boundary-note">
          These are boundaries, not gaps waiting to be filled. Several of them are what keep InBrief
          outside the medical device regime. InBrief is not affiliated with or endorsed by the NHS.
        </p>
      </Band>

      <Band id="faq" title="Questions we get asked"
        lede="The ones that decide whether a procurement conversation happens. Answered from
              what the software does, not from what would sell well.">
        <Faq />
      </Band>

      <Band tone="deep">
        <div className="mk-final">
          <h2 className="mk-final-title">See it against your own contract.</h2>
          <p className="mk-final-lede">
            A demonstration takes half an hour. A paid pilot runs twelve weeks and starts at{' '}
            {money(PILOTS[0].priceGbp)}, credited against an annual subscription if you convert.
          </p>
          <div className="mk-hero-actions">
            <Anchor to="/contact" className="btn btn-primary">Book a demo</Anchor>
            <Anchor to="/pricing" className="btn btn-secondary">See pricing</Anchor>
          </div>
        </div>
      </Band>
    </>
  );
}
